import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Space, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore } from '@fortawesome/free-solid-svg-icons';
import type { AppDispatch, RootState } from '../../redux/store';
import {
  fetchStores,
  fetchCategories,
  fetchSchedules,
  createStore,
  updateStore,
  deleteStore,
} from '../../redux/UserStores';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import { ConfigProvider } from 'antd';
import {
  Container,
  HeaderSection,
  Title,
  Subtitle,
  PrimaryButton,
  LastUpdatedContainer,
  LastUpdatedLabel,
  LastUpdatedValue,
  TitleIcon,
  StyledButton,
} from './styles';
import StoreStats from './StoreStats';
import StoreTable from './StoreTable';
import StoreForm from './StoreForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import type { StoresProps } from './types';
import type { Store, CreateStorePayload, UpdateStorePayload } from '../../redux/UserStores';
import { LABELS, MESSAGES } from './constants';

const Stores: React.FC<StoresProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const theme = useMemo(() => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }), [isDarkMode]);

  const { stores, categories, schedules, loading } = useSelector(
    (state: RootState) => state.stores
  );

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<{ id: string; name: string } | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllData = async () => {
      await Promise.all([
        dispatch(fetchStores({})),
        dispatch(fetchCategories()),
        dispatch(fetchSchedules()),
      ]);
      setLastUpdated(new Date().toLocaleString());
    };
    fetchAllData();
  }, [dispatch]);

  const handleCreate = useCallback(() => {
    setSelectedStore(null);
    setIsFormOpen(true);
  }, []);

  const handleEdit = useCallback((store: Store) => {
    setSelectedStore(store);
    setIsFormOpen(true);
  }, []);

  const handleDelete = useCallback((storeId: string) => {
    const store = stores.find((s) => s.id === storeId);
    if (store) {
      setStoreToDelete({ id: storeId, name: store.name });
      setIsDeleteDialogOpen(true);
    }
  }, [stores]);

  const handleFormSubmit = useCallback(
    async (data: CreateStorePayload | UpdateStorePayload) => {
      try {
        if ('id' in data) {
          await dispatch(updateStore(data as UpdateStorePayload)).unwrap();
          message.success(MESSAGES.STORE_UPDATED);
        } else {
          await dispatch(createStore(data)).unwrap();
          message.success(MESSAGES.STORE_CREATED);
        }
        setIsFormOpen(false);
        setSelectedStore(null);
        setLastUpdated(new Date().toLocaleString());
      } catch (error: any) {
        message.error(error || 'Failed to save store');
      }
    },
    [dispatch]
  );

  const handleFormCancel = useCallback(() => {
    setIsFormOpen(false);
    setSelectedStore(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!storeToDelete) return;

    try {
      await dispatch(deleteStore(storeToDelete.id)).unwrap();
      message.success(MESSAGES.STORE_DELETED);
      setIsDeleteDialogOpen(false);
      setStoreToDelete(null);
      setLastUpdated(new Date().toLocaleString());
    } catch (error: any) {
      message.error(error || 'Failed to delete store');
    }
  }, [dispatch, storeToDelete]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchStores({}));
    setLastUpdated(new Date().toLocaleString());
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={1}>
                <TitleIcon>
                  <FontAwesomeIcon icon={faStore} />
                </TitleIcon>
                {LABELS.STORES_MANAGEMENT || 'Stores Management'}
              </Title>
              <Subtitle>
                Manage all your store locations, add new ones, edit existing details, and organize your business operations efficiently.
              </Subtitle>
            </div>
            <Space>
              {lastUpdated && (
                <LastUpdatedContainer>
                  <LastUpdatedLabel>
                    {LABELS.LAST_UPDATED}
                  </LastUpdatedLabel>
                  <LastUpdatedValue>
                    {lastUpdated}
                  </LastUpdatedValue>
                </LastUpdatedContainer>
              )}
              <StyledButton
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={loading.stores}
              >
                {LABELS.REFRESH}
              </StyledButton>
              <PrimaryButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                {LABELS.CREATE_STORE}
              </PrimaryButton>
            </Space>
          </HeaderSection>

          <StoreStats stores={stores} />

          <StoreTable
            stores={stores}
            loading={loading.stores}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {isFormOpen && (
            <StoreForm
              store={selectedStore}
              categories={categories}
              schedules={schedules}
              loading={loading.create || loading.update}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          )}

          {isDeleteDialogOpen && storeToDelete && (
            <DeleteConfirmDialog
              open={isDeleteDialogOpen}
              storeName={storeToDelete.name}
              onConfirm={handleDeleteConfirm}
              onCancel={() => {
                setIsDeleteDialogOpen(false);
                setStoreToDelete(null);
              }}
              loading={loading.delete}
            />
          )}
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Stores);
